// ===========================================================================
// ReHome — Auth Context & Provider
// Wraps the app with Supabase Auth session management.
// ===========================================================================

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from './supabase';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  full_name: string | null;
  user_type: 'platform' | 'shelter';
  password_change_required: boolean;
  role: string | null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signInWithGoogle: (redirectPath?: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
  clearError: () => void;
}

// ─── Context ────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── Provider ───────────────────────────────────────────────────────────────

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    userProfile: null,
    loading: true,
    error: null,
  });

  // Fetch user profile from user_profiles table
  const fetchUserProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    if (!isSupabaseConfigured()) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, full_name, user_type, password_change_required, role')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data) {
      console.warn('Failed to fetch user profile:', error?.message);
      return null;
    }
    return data as UserProfile;
  }, []);

  // Initialize: check for existing session
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      let profile: UserProfile | null = null;
      if (session?.user) {
        profile = await fetchUserProfile(session.user.id);
      }
      setState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
        userProfile: profile,
        loading: false,
      }));
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      let profile: UserProfile | null = null;
      if (session?.user) {
        profile = await fetchUserProfile(session.user.id);
      }
      setState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
        userProfile: profile,
        loading: false,
      }));
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  // ─── Auth Actions ────────────────────────────────────────────────────────

  const signUp = useCallback(async (email: string, password: string, fullName?: string) => {
    if (!isSupabaseConfigured()) {
      return { error: 'A Supabase nincs konfigurálva. Indítsd el a `supabase start` parancsot.' };
    }
    setState(prev => ({ ...prev, error: null }));

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName || email.split('@')[0],
          user_type: 'platform',
        },
      },
    });

    if (error) {
      setState(prev => ({ ...prev, error: translateAuthError(error) }));
      return { error: translateAuthError(error) };
    }

    // Profile is auto-created by the handle_new_user trigger in the DB.
    return { error: null };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!isSupabaseConfigured()) {
      return { error: 'A Supabase nincs konfigurálva. Indítsd el a `supabase start` parancsot.' };
    }
    setState(prev => ({ ...prev, error: null }));

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      setState(prev => ({ ...prev, error: translateAuthError(error) }));
      return { error: translateAuthError(error) };
    }

    return { error: null };
  }, []);

  /**
   * Google OAuth belépés.
   *
   * @param redirectPath alkalmazáson belüli útvonal, ahová az OAuth kör után
   *   vissza kell térni (pl. `/adoption/123`). Enélkül a felhasználó mindig a
   *   nyitóoldalon köt ki, és elveszti azt, amit épp csinálni akart. Csak
   *   relatív, egy `/`-rel kezdődő útvonalat fogadunk el — a `//host` és a
   *   teljes URL-ek nyílt átirányítási (open redirect) rést nyitnának.
   */
  const signInWithGoogle = useCallback(async (redirectPath?: string) => {
    if (!isSupabaseConfigured()) return;
    const isSafeInternalPath = !!redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('//');
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: isSafeInternalPath ? `${window.location.origin}${redirectPath}` : window.location.origin,
      },
    });
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    if (!isSupabaseConfigured()) {
      return { error: 'A Supabase nincs konfigurálva.' };
    }
    // A cél NYILVÁNOS oldal kell legyen. A `/profile` bejelentkezést követel:
    // a helyreállító munkamenet az URL fragmentjéből épül fel, aszinkron, és
    // amíg ez tart, az útvonalőr átirányít a bejelentkezésre — `pathname +
    // search` alapján, ami a FRAGMENTET eldobja. A token ezzel elveszik, a
    // felhasználó pedig épp azon a képernyőn köt ki, ahova nem tud belépni.
    // Az admin appban ráadásul `/profile` útvonal nem is létezik.
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) return { error: translateAuthError(error) };
    return { error: null };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    if (!isSupabaseConfigured()) {
      return { error: 'A Supabase nincs konfigurálva.' };
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { error: translateAuthError(error) };

    // Mark password_change_required = false
    const { error: profileError } = await supabase
      .from('user_profiles')
      .update({ password_change_required: false })
      .eq('id', state.user?.id);

    if (profileError) {
      console.warn('Failed to update password_change_required:', profileError.message);
    }
    // Refresh user profile in state
    if (state.user) {
      const profile = await fetchUserProfile(state.user.id);
      setState(prev => ({ ...prev, userProfile: profile }));
    }
    return { error: null };
  }, [state.user, fetchUserProfile]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, signUp, signIn, signInWithGoogle, signOut, resetPassword, updatePassword, clearError }}>
      {children}
    </AuthContext.Provider>
  );
};

// ─── Hook ───────────────────────────────────────────────────────────────────

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Translate Supabase error codes to Hungarian user-friendly messages. */
function translateAuthError(error: AuthError): string {
  const fallback = 'Ismeretlen hiba történt. Próbáld újra.';
  const rawMessage = error.message || '';
  const msg = rawMessage.toLowerCase();

  if (msg.includes('invalid login credentials') || msg.includes('invalid email or password')) {
    return 'Hibás email cím vagy jelszó.';
  }
  if (msg.includes('email not confirmed')) {
    return 'Az email cím még nincs megerősítve. Kérlek ellenőrizd a postaládádat.';
  }
  if (msg.includes('already registered') || msg.includes('already exists')) {
    return 'Ezzel az email címmel már regisztráltak.';
  }
  if (msg.includes('password')) {
    return 'A jelszónak legalább 8 karakter hosszúnak kell lennie.';
  }
  if (msg.includes('rate limit') || msg.includes('too many requests')) {
    return 'Túl sok próbálkozás. Kérlek várj egy percet.';
  }
  if (msg.includes('confirmation') && msg.includes('email')) {
    return 'Nem sikerült elküldeni a megerősítő emailt. Kérlek próbáld újra pár perc múlva.';
  }

  // Defensive fallback: some supabase-js client errors (e.g. AuthRetryableFetchError
  // for certain 5xx responses) can surface a non-informative .message — such as
  // the literal string "{}" — instead of a real sentence. Never show that raw
  // value to the user; fall back to a generic message instead.
  const looksLikeRawObject = !rawMessage.trim() || /^\{.*\}$/.test(rawMessage.trim());
  return looksLikeRawObject ? fallback : rawMessage;
}
